<template>
    <div :class="[colClass]">
        <div class="DFRRegionHeading" v-if="effectiveLabel">
            <h2 class="df-section-title">
                {{ effectiveLabel }}
                <LoadingWheel :width="20" v-if="loading" class="d-inline-block" />
            </h2>
        </div>
        <div class="clearfix" style="margin-bottom: 10px">
            <div>
                <div class="dfForEachItem clearfix" v-for="childObj in childrenObj" :key="childObj.child.vmId">
                    <div class="form-group col-md-12">
                        <ChildrenList :children-ids="childObj.childChildren" :view-only="isViewOnly"></ChildrenList>
                        <button type="button" class="btn btn-link dfNavButton dfNavRemoveButton" @click="removeItem(childObj.child)" v-if="(!isComputed || canAddRemoveItems) && !isViewOnly && !isEqualMinMax" :disabled="effectiveMin != null && effectiveMin >= childrenObj.length">
                            <span aria-hidden="true">&times;</span>
                            <span class="sr-only">{{ settings.removeText }}</span>
                        </button>
                    </div>
                </div>
            </div>
            <div class="form-group" v-if="(!isComputed || canAddRemoveItems) && !isViewOnly && !isEqualMinMax">
                <button type="button" class="btn btn-outline-secondary dfNavButton" data-test-id="add-button" @click="addItem" :disabled="effectiveMax != null && effectiveMax <= childrenObj.length">
                    <span class="plus-sign" aria-hidden="true">+</span>
                    {{ settings.addText }}
                </button>
            </div>
        </div>
        <ErrorList :data="data" />
        <AdminSettingsDifferences :data="data" />
    </div>
</template>
<script lang="ts">
import { defineComponent, ref, watch, computed } from "vue";
//import { useMainStore } from "@/mainStore";
//import { Observable } from "rxjs";
import ForEachDef, { ForEachDefItem } from "../elements/ForEachDef";
import { ForEachSettings } from "@cs-ts/for-each-settings";
import { useObservable } from "@vueuse/rxjs";
import { props, Props, components, setup } from "./BaseDefMixin.3";
import LoadingWheel from "@/lib/LoadingWheel.3.vue";
import arrayEquals from "@/lib/arrayEquals";
import { useMainStore } from "@/mainStore";

export default defineComponent({
    components: {
        LoadingWheel,
        ...components,
    },
    props: props,
    //@ts-ignore
    setup(props: Props<ForEachDef, ForEachSettings>) {
        const mainStore = useMainStore();
        const base = setup<ForEachDef, ForEachSettings>(props);

        const loading = ref(false);
        const oldIds = ref();
        const calculatedIds = ref<string[] | undefined>([]);
        const effectiveMin = useObservable(props.data.min$());
        const effectiveMax = useObservable(props.data.max$());
        const effectiveLabel = useObservable(props.data.effectiveLabel$());

        const isComputed = props.data.definition.settings.calculationId != null;
        const canAddRemoveItems = computed(() => {
            return props.data.definition.settings.addRemoveItems;
        });
        const childrenObj = computed(() => {
            return (props.data.children as ForEachDefItem[])
                .filter(x => x.removedChild == undefined || x.removedChild == false)
                .map(id => base.pageStore.getElementByVmId(id))
                .map(child => ({
                    child,
                    childChildren: base.pageStore.getChildrenByVmId(child!.vmId)!.filter(el => base.elementFilter(el)),
                }));
        });
        const isEqualMinMax = computed(() => {
            return effectiveMin.value != null && effectiveMin.value == effectiveMax.value && effectiveMin.value == childrenObj.value.length;
        });

        const addItem = () => {
            base.pageStore.addForEachItem(base.vmId.value);
            mainStore.setLiveRegion(Promise.resolve("Item has been added successfully."));
        };
        const removeItem = (item: ForEachDefItem) => {
            base.pageStore.removeForEachItem({ forEachVmId: base.vmId.value, item: item, calculatedItems: calculatedIds.value ?? [] });
            mainStore.setLiveRegion(Promise.resolve("Item has been deleted successfully."));
        };
        const setCalculatedItems = async (newIds: string[] | undefined) => {
            if (newIds == null || arrayEquals(newIds, oldIds.value)) {
                return;
            }
            // Don't show loading on first load
            if (oldIds.value != null) {
                // Fake loading for UX since we don't know when real loading started
                loading.value = true;
                setTimeout(() => {
                    loading.value = false;
                }, 400);
            }
            oldIds.value = newIds;
            base.pageStore.setForEachChildren({ forEachVmId: base.vmId.value, ids: newIds });
        };

        if (isComputed) {
            const calculatedItemIds = useObservable(props.data.resolveCalculation(props.data.definition.settings.calculationId!));
            watch(calculatedItemIds, itemIds => {
                calculatedIds.value = itemIds;
                setCalculatedItems(itemIds);
            });
        }

        let firstEffectiveMin = true;
        setTimeout(() => {
            firstEffectiveMin = false;
        }, 100);
        watch(
            effectiveMin,
            min => {
                let added = 0;
                let iteration = 0;
                while (min != null && childrenObj.value.length < min) {
                    base.pageStore.addForEachItem(base.vmId.value);
                    added++;
                    if (iteration++ > 100) throw new Error("Infinite loop");
                }
                if (added > 0 && !firstEffectiveMin) {
                    mainStore.setLiveRegion(Promise.resolve(`${added} ${added > 1 ? "items have" : "item has"} been automatically added.`));
                }
            },
            { immediate: true },
        );

        return {
            loading,
            effectiveLabel,
            isComputed,
            childrenObj,
            canAddRemoveItems,
            effectiveMin,
            effectiveMax,
            isEqualMinMax,
            addItem,
            removeItem,

            ...base.expose,
        };
    },
});
</script>
